import React, { useEffect, useState } from 'react';
import { getAdminSchema, getStatsAccess, runAdminQuery } from '../api';

const DEFAULT_SQL = `SELECT created_at, strain_name, brand_name, consumption_style, buy_again
FROM consumption_logs
ORDER BY created_at DESC
LIMIT 25;`;

function formatCell(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function toCsv(columns, rows) {
  const escape = (value) => {
    const text = formatCell(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return [
    columns.map(escape).join(','),
    ...rows.map((row) => columns.map((column) => escape(row[column])).join(',')),
  ].join('\n');
}

function ResultsTable({ result }) {
  if (!result) return null;

  if (!result.rowCount) {
    return <p className="subtitle">Query ran in {result.elapsedMs}ms and returned no rows.</p>;
  }

  return (
    <>
      <p className="subtitle">
        {result.rowCount} row{result.rowCount === 1 ? '' : 's'} in {result.elapsedMs}ms
        {result.truncated ? ` — showing the first ${result.maxRows}` : ''}
      </p>
      <div className="query-results">
        <table>
          <thead>
            <tr>
              {result.columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, index) => (
              <tr key={index}>
                {result.columns.map((column) => (
                  <td key={column}>{formatCell(row[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SchemaPanel({ tables, onInsert }) {
  const [openTable, setOpenTable] = useState('');

  return (
    <section className="dashboard-section">
      <h2>Tables</h2>
      <p className="subtitle">Tap a table to see its columns, or to start a query against it.</p>
      <ul className="schema-list">
        {tables.map((table) => (
          <li key={table.name}>
            <button
              type="button"
              className="schema-table-button"
              onClick={() => setOpenTable(openTable === table.name ? '' : table.name)}
            >
              {table.name}
              <span className="schema-count">{table.rowCount} rows</span>
            </button>
            {openTable === table.name ? (
              <div className="schema-columns">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => onInsert(`SELECT * FROM ${table.name} LIMIT 25;`)}
                >
                  Query this table
                </button>
                <ul>
                  {table.columns.map((column) => (
                    <li key={column.name}>
                      <code>{column.name}</code> {column.type}
                      {column.redacted ? <em> — redacted</em> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function AdminQuery({ user, onBack }) {
  const [allowed, setAllowed] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [tables, setTables] = useState([]);
  const [examples, setExamples] = useState([]);
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    getStatsAccess(user.id)
      .then((data) => setAllowed(Boolean(data.allowed)))
      .catch(() => setAllowed(false))
      .finally(() => setAccessChecked(true));
  }, [user]);

  useEffect(() => {
    if (!user?.id || !allowed) return;
    getAdminSchema(user.id)
      .then((data) => {
        setTables(data.tables || []);
        setExamples(data.examples || []);
      })
      .catch((err) => setError(err.error || 'Unable to load the database schema'));
  }, [user, allowed]);

  const handleRun = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setRunning(true);
    try {
      const data = await runAdminQuery(user.id, sql);
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(err.error || 'Unable to run that query');
    } finally {
      setRunning(false);
    }
  };

  const handleDownload = () => {
    if (!result?.rowCount) return;
    const blob = new Blob([toCsv(result.columns, result.rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `my-weed-buddy-query-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!accessChecked) {
    return (
      <div className="container wide">
        <p className="brand">My Weed Buddy</p>
        <h1>Database query</h1>
        <p className="subtitle">Loading...</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="container wide">
        <p className="brand">My Weed Buddy</p>
        <h1>Database query</h1>
        <p className="subtitle">This page is not available for your account.</p>
        <button type="button" className="secondary" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container dashboard">
      <p className="brand">My Weed Buddy</p>
      <h1>Database query</h1>
      <p className="subtitle">
        Read-only. Only SELECT and WITH queries run here, and passwords and activation tokens are
        always redacted.
      </p>
      <button type="button" className="secondary" onClick={onBack}>
        Back to welcome
      </button>

      <section className="dashboard-section">
        <h2>Run a query</h2>
        {examples.length ? (
          <div className="query-examples">
            {examples.map((example) => (
              <button
                key={example.label}
                type="button"
                className="secondary"
                onClick={() => setSql(example.sql)}
              >
                {example.label}
              </button>
            ))}
          </div>
        ) : null}
        <form onSubmit={handleRun}>
          <div className="form-group">
            <label htmlFor="sql">SQL</label>
            <textarea
              id="sql"
              className="query-input"
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              rows={8}
              spellCheck={false}
            />
          </div>
          <div className="log-edit-actions">
            <button type="submit" disabled={running}>
              {running ? 'Running...' : 'Run query'}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleDownload}
              disabled={!result?.rowCount}
            >
              Download CSV
            </button>
          </div>
        </form>
        {error ? <div className="error">{error}</div> : null}
        <ResultsTable result={result} />
      </section>

      <SchemaPanel tables={tables} onInsert={setSql} />
    </div>
  );
}

export default AdminQuery;
